import {
    CandlestickIndicatorInput,
    IndicatorCategory,
    IndicatorMeasure,
    IndicatorPane,
    type IndicatorCandle,
    type IndicatorDefinition,
    type IndicatorOutputValue,
    type IndicatorProcessInput,
} from '../indicator-definition.js';
import { registerIndicator } from '../indicator-registry.js';
import {
    SequentialIndicatorProcessor,
    type IndicatorCalculationResult,
} from '../sequential-processor.js';
import {
    RingBuffer,
    SmoothedMovingAverage,
    type RingBufferCheckpoint,
    type SmoothedMovingAverageCheckpoint,
} from '../math/index.js';
import {
    AlligatorParameters,
    alligatorParameterSchema,
    lineStyle,
    period,
    validWindow,
} from './shared/shifted-sparse.js';
import {
    finite,
} from './shared/guards.js';

export interface AlligatorCheckpoint {
    readonly jaw: SmoothedMovingAverageCheckpoint;
    readonly teeth: SmoothedMovingAverageCheckpoint;
    readonly lips: SmoothedMovingAverageCheckpoint;
    readonly jawDelay: RingBufferCheckpoint<number>;
    readonly teethDelay: RingBufferCheckpoint<number>;
    readonly lipsDelay: RingBufferCheckpoint<number>;
}

export class AlligatorProcessor extends SequentialIndicatorProcessor<
    IndicatorCandle,
    AlligatorCheckpoint
> {
    private readonly jaw: SmoothedMovingAverage;
    private readonly teeth: SmoothedMovingAverage;
    private readonly lips: SmoothedMovingAverage;
    private readonly jawDelay: RingBuffer<number>;
    private readonly teethDelay: RingBuffer<number>;
    private readonly lipsDelay: RingBuffer<number>;

    constructor(
        readonly jawLength: number,
        readonly jawShift: number,
        readonly teethLength: number,
        readonly teethShift: number,
        readonly lipsLength: number,
        readonly lipsShift: number,
    ) {
        super(['jaw', 'teeth', 'lips']);
        period(jawLength, jawLength, 1, 200, 'jawLength');
        period(jawShift, jawShift, 0, 100, 'jawShift');
        period(teethLength, teethLength, 1, 200, 'teethLength');
        period(teethShift, teethShift, 0, 100, 'teethShift');
        period(lipsLength, lipsLength, 1, 200, 'lipsLength');
        period(lipsShift, lipsShift, 0, 100, 'lipsShift');
        this.jaw = new SmoothedMovingAverage(jawLength);
        this.teeth = new SmoothedMovingAverage(teethLength);
        this.lips = new SmoothedMovingAverage(lipsLength);
        this.jawDelay = new RingBuffer(jawShift + 1);
        this.teethDelay = new RingBuffer(teethShift + 1);
        this.lipsDelay = new RingBuffer(lipsShift + 1);
    }

    protected calculate(
        input: IndicatorProcessInput<IndicatorCandle>,
        commit: boolean,
    ): IndicatorCalculationResult {
        const high = finite(input.value?.high);
        const low = finite(input.value?.low);
        const median = high === null || low === null ? null : (high + low) / 2;
        const jaw = this.line(this.jaw, this.jawDelay, this.jawShift, median, commit);
        const teeth = this.line(this.teeth, this.teethDelay, this.teethShift, median, commit);
        const lips = this.line(this.lips, this.lipsDelay, this.lipsShift, median, commit);
        const values: IndicatorOutputValue[] = [];
        // A commit reports the average of the bar it just closed, drawn `shift` bars ahead. A
        // preview reports an average committed `shift` bars ago, which already belongs to the bar
        // being previewed -- the shift is spent, so it is not applied a second time.
        const shifted = (shift: number) => (commit ? input.index + shift : input.index);
        if (input.index >= this.jawLength - 1)
            values.push(this.formedOutput('jaw', jaw, this.jaw.isFormed, shifted(this.jawShift)));
        if (input.index >= this.teethLength - 1)
            values.push(this.formedOutput('teeth', teeth, this.teeth.isFormed, shifted(this.teethShift)));
        if (input.index >= this.lipsLength - 1)
            values.push(this.formedOutput('lips', lips, this.lips.isFormed, shifted(this.lipsShift)));
        return {
            isFormed: this.jaw.isFormed,
            values,
        };
    }

    // One line's answer on both paths. A commit feeds its average into the buffer that
    // `AlligatorLine.Buffer` is; the caller draws it `shift` bars ahead. A forming bar feeds it
    // nothing: StockSharp discards the average computed for such a bar and answers `Buffer[1]`,
    // the average already committed for the bar being previewed, and only while
    // `Buffer.Count > Shift` -- so the first point of a shifted line appears when its bar closes.
    private line(
        average: SmoothedMovingAverage,
        delay: RingBuffer<number>,
        shift: number,
        median: number | null,
        commit: boolean,
    ): number | null {
        if (!commit) {
            // Shift 0 has no committed average standing in for the forming bar -- StockSharp
            // rejects Shift < 1 -- so an unshifted line previews itself, like every average here.
            if (shift === 0) return average.preview(median);
            return delay.size > shift ? (delay.at(1) ?? null) : null;
        }
        const value = average.push(median);
        if (value !== null && average.isFormed) delay.push(value);
        return value;
    }

    protected resetState(): void {
        this.jaw.reset();
        this.teeth.reset();
        this.lips.reset();
        this.jawDelay.clear();
        this.teethDelay.clear();
        this.lipsDelay.clear();
    }
    protected captureState(): AlligatorCheckpoint {
        return Object.freeze({
            jaw: this.jaw.checkpoint(),
            teeth: this.teeth.checkpoint(),
            lips: this.lips.checkpoint(),
            jawDelay: this.jawDelay.checkpoint(),
            teethDelay: this.teethDelay.checkpoint(),
            lipsDelay: this.lipsDelay.checkpoint(),
        });
    }
    protected restoreState(state: AlligatorCheckpoint): void {
        if (state === null || typeof state !== 'object'
            || !validWindow(state.jawDelay, this.jawShift + 1)
            || !validWindow(state.teethDelay, this.teethShift + 1)
            || !validWindow(state.lipsDelay, this.lipsShift + 1)) {
            throw new TypeError('sschart: invalid Alligator checkpoint');
        }
        this.jaw.restore(state.jaw);
        this.teeth.restore(state.teeth);
        this.lips.restore(state.lips);
        this.jawDelay.restore(state.jawDelay);
        this.teethDelay.restore(state.teethDelay);
        this.lipsDelay.restore(state.lipsDelay);
    }
}

export const AlligatorIndicator: IndicatorDefinition<
    IndicatorCandle,
    AlligatorParameters
> = registerIndicator({
    id: 'Alligator',
    name: 'Alligator',
    description: 'Bill Williams median-price SMMA lines with independent forward shifts.',
    category: IndicatorCategory.Trend,
    input: CandlestickIndicatorInput,
    parameters: alligatorParameterSchema(),
    outputs: [
        { id: 'jaw', name: 'Jaw', defaultStyle: lineStyle('#1E90FF') },
        { id: 'teeth', name: 'Teeth', defaultStyle: lineStyle('#FF0000') },
        { id: 'lips', name: 'Lips', defaultStyle: lineStyle('#32CD32') },
    ],
    naturalPane: IndicatorPane.Overlay,
    measure: IndicatorMeasure.Price,
    aliases: ['alligator'],
    painter: 'alligator',
    processorFactory: (parameters) => new AlligatorProcessor(
        period(parameters?.jawLength, 13, 1, 200, 'jawLength'),
        period(parameters?.jawShift, 8, 0, 100, 'jawShift'),
        period(parameters?.teethLength, 8, 1, 200, 'teethLength'),
        period(parameters?.teethShift, 5, 0, 100, 'teethShift'),
        period(parameters?.lipsLength, 5, 1, 200, 'lipsLength'),
        period(parameters?.lipsShift, 3, 0, 100, 'lipsShift'),
    ),
});
